/**
 * FP3-035C — Reference Library page.
 *
 * Provider-facing surface for structured reference data the AI can
 * lean on (currently the Iowa-Nebraska articulation norms). Role
 * visibility is enforced server-side; this page just renders what
 * the API hands back.
 *
 * Lives at /references. Sidebar entry "Reference Library".
 */
import { useEffect, useState } from "react";
import { Books, Info } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PageShell from "@/components/layout/PageShell";

function PositionBadge({ scope }) {
  const meta = {
    initial: { label: "Initial only", className: "bg-[#E6EFF7] text-[#1F3A5A] border-[#1F3A5A]/20" },
    final:   { label: "Final only", className: "bg-[#FAE9DC] text-[#7A4622] border-[#7A4622]/20" },
    both:    { label: "Both", className: "bg-[#E8F0EB] text-[#1E3F33] border-[#1E3F33]/20" },
    vocalic: { label: "Vocalic", className: "bg-[#F1EEF6] text-[#4A3F66] border-[#4A3F66]/20" },
    cluster: { label: "Cluster", className: "bg-[#FAF1DD] text-[#7A5B1F] border-[#7A5B1F]/20" },
  }[scope] || { label: scope || "—", className: "bg-[#F3F1EC] text-[#6B7280] border-[#9B9890]/20" };
  return (
    <Badge
      variant="outline"
      className={`text-[10px] font-semibold tracking-wide uppercase ${meta.className}`}
    >
      {meta.label}
    </Badge>
  );
}

function NormTable({ rows, testid }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[#E5E3DB] bg-white" data-testid={testid}>
      <table className="min-w-full text-sm">
        <thead className="bg-[#F4F2EC] text-[#6B7280] uppercase tracking-wide text-[10px]">
          <tr>
            <th className="px-3 py-2 text-left">Sound</th>
            <th className="px-3 py-2 text-left">Example</th>
            <th className="px-3 py-2 text-left">Position</th>
            <th className="px-3 py-2 text-right">Females</th>
            <th className="px-3 py-2 text-right">Males</th>
            <th className="px-3 py-2 text-left">Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.sound_key}
              data-testid={`${testid}-row-${r.sound_key}`}
              className={`border-t border-[#ECEAE4] ${
                r.special_handling ? "bg-[#FFF7E6]" : "bg-white"
              }`}
            >
              <td className="px-3 py-2 font-mono text-[#1F2937]">
                {r.display_sound}
                {r.special_handling && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-[9px] font-semibold tracking-wide uppercase bg-[#FAE9DC] text-[#7A4622] border-[#7A4622]/30"
                  >
                    Caution
                  </Badge>
                )}
              </td>
              <td className="px-3 py-2 text-[#4B5563] italic">{r.example || "—"}</td>
              <td className="px-3 py-2"><PositionBadge scope={r.position_scope} /></td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-[#1F2937]">
                {r.female_age_label || "—"}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-[#1F2937]">
                {r.male_age_label || "—"}
              </td>
              <td className="px-3 py-2 text-[11px] text-[#6B7280] max-w-md">{r.notes || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReferenceLibrary() {
  const [normSets, setNormSets] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Initial load — list visible norm sets, then load the first one's detail.
  useEffect(() => {
    setLoading(true);
    api
      .listReferenceNormSets()
      .then((r) => {
        const sets = r.data?.norm_sets || [];
        setNormSets(sets);
        if (sets.length > 0) setActiveId(sets[0].id);
      })
      .catch(() => setError("Could not load reference library."))
      .finally(() => setLoading(false));
  }, []);

  // Load full detail whenever the user picks a different norm set.
  useEffect(() => {
    if (!activeId) return;
    setDetail(null);
    api
      .getReferenceNormSet(activeId)
      .then((r) => setDetail(r.data))
      .catch((e) => {
        setError(
          e?.response?.status === 403
            ? "This reference is restricted to specific provider roles."
            : "Could not load reference detail.",
        );
      });
  }, [activeId]);

  return (
    <PageShell
      testId="reference-library-page"
      variant="table"
      className="space-y-5"
    >
      <header className="flex items-start gap-3 flex-wrap">
        <Books size={28} weight="duotone" className="text-[#1E3F33] mt-1" />
        <div>
          <h1 className="text-2xl font-semibold text-[#1F2937] heading-font">
            Reference Library
          </h1>
          <p className="text-sm text-[#6B7280] mt-0.5 max-w-2xl">
            Structured reference data the platform uses for
            evidence-based interpretation. The AI never invents norms —
            it must read from the rows below.
          </p>
        </div>
      </header>

      {loading && (
        <p className="text-xs text-[#9B9890]" data-testid="reference-library-loading">
          Loading…
        </p>
      )}

      {!loading && normSets.length === 0 && !error && (
        <Card
          className="p-6 border-[#E5E3DB] bg-white"
          data-testid="reference-library-empty"
        >
          <div className="flex items-start gap-3">
            <Info size={20} weight="duotone" className="text-[#6B7280] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#1F2937]">
                No references available for your role yet.
              </p>
              <p className="text-xs text-[#6B7280] mt-1">
                The Iowa-Nebraska articulation norms are visible to
                SLP and SLPA providers. If you should have access,
                contact your administrator.
              </p>
            </div>
          </div>
        </Card>
      )}

      {error && (
        <Card
          className="p-4 border-[#E5BBB1] bg-[#FBE7DC] text-[#7A3A2C]"
          data-testid="reference-library-error"
        >
          {error}
        </Card>
      )}

      {!loading && normSets.length > 0 && (
        <>
          {/* Tabs (only when >1 set is visible) */}
          {normSets.length > 1 && (
            <div className="inline-flex rounded-lg border border-[#E5E3DB] bg-white overflow-hidden">
              {normSets.map((s) => (
                <button
                  key={s.id}
                  data-testid={`reference-tab-${s.id}`}
                  onClick={() => setActiveId(s.id)}
                  className={`px-4 py-2 text-sm font-medium transition ${
                    activeId === s.id
                      ? "bg-[#1E3F33] text-white"
                      : "text-[#1F2937] hover:bg-[#F4F2EC]"
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </div>
          )}

          {detail && (
            <article
              className="space-y-5"
              data-testid={`reference-norm-set-${detail.id}`}
            >
              <Card className="p-4 border-[#E5E3DB] bg-white">
                <h2
                  className="text-lg font-semibold text-[#1F2937] heading-font"
                  data-testid="reference-norm-title"
                >
                  {detail.title}
                </h2>
                <p
                  className="text-xs text-[#6B7280] mt-1 italic"
                  data-testid="reference-norm-source"
                >
                  {detail.source}
                </p>
                <p className="text-sm text-[#1F2937] mt-2">{detail.description}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(detail.role_visibility || []).map((r) => (
                    <Badge
                      key={r}
                      variant="outline"
                      className="text-[10px] bg-[#F1EEF6] text-[#4A3F66] border-[#4A3F66]/20"
                    >
                      {r}
                    </Badge>
                  ))}
                </div>
              </Card>

              <section data-testid="reference-phoneme-section">
                <h3 className="text-sm font-semibold text-[#1F2937] mb-2">
                  Phoneme norms — singleton consonants (90% criterion)
                </h3>
                <NormTable
                  rows={detail.phoneme_norms || []}
                  testid="reference-phoneme-table"
                />
              </section>

              <section data-testid="reference-cluster-section">
                <h3 className="text-sm font-semibold text-[#1F2937] mb-2">
                  Cluster norms — word-initial consonant clusters
                </h3>
                <NormTable
                  rows={detail.cluster_norms || []}
                  testid="reference-cluster-table"
                />
              </section>

              <section data-testid="reference-notes-section">
                <h3 className="text-sm font-semibold text-[#1F2937] mb-2">
                  Interpretation notes
                </h3>
                <div className="space-y-3">
                  {(detail.interpretation_notes || []).map((n) => (
                    <Card
                      key={n.title}
                      className="p-3 border-[#E5E3DB] bg-white"
                      data-testid={`reference-note-${n.title
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-|-$/g, "")}`}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-1">
                        {n.title}
                      </p>
                      <p className="text-sm text-[#1F2937] leading-relaxed">{n.body}</p>
                    </Card>
                  ))}
                </div>
              </section>
            </article>
          )}
        </>
      )}
    </PageShell>
  );
}
